import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Button from '../components/ui-kit/components/Button';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import BlockIcon from '@mui/icons-material/Block';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import { getSystemStats } from '../api/statistics';
import { exportToDropbox, getSyncStatus } from '../api/sync';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Fetch system statistics — accessible to all authenticated users,
  // backend returns only data the user has permission to see
  const {
    data: statsData,
    isLoading: isLoadingStats,
    error: statsError,
  } = useQuery(['systemStats'], getSystemStats, {
    enabled: !permissionsLoading,
  });

  // Fetch sync status
  const {
    data: syncData,
  } = useQuery(['syncStatus'], getSyncStatus, {
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Mutation for exporting to Dropbox
  const exportMutation = useMutation(exportToDropbox, {
    onSuccess: (data) => {
      const filesInfo = data.data.files && data.data.files.length > 0
        ? `${data.data.files.length} files exported`
        : 'Data exported';

      setSnackbar({
        open: true,
        message: `${filesInfo} successfully! Processed ${data.data.processed} users in ${data.data.duration}`,
        severity: 'success',
      });
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.error || 'Failed to export database';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    },
  });

  const handleExportToDropbox = () => {
    exportMutation.mutate();
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Show loading state while permissions are being loaded
  if (permissionsLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading permissions...
        </Typography>
      </Box>
    );
  }

  // Navigation handlers for cards with filters
  const handleNavigateToUsers = (filter) => {
    if (!hasPermission('auth-service', 'user', 'list')) return;
    let url = '/users';
    if (filter === 'active') url += '?isActive=true';
    if (filter === 'blocked') url += '?isActive=false';
    navigate(url);
  };

  const handleNavigateToRoles = () => {
    if (!hasPermission('auth-service', 'role', 'list')) return;
    navigate('/roles');
  };

  const handleNavigateToPermissions = () => {
    if (!hasPermission('auth-service', 'permission', 'list')) return;
    navigate('/permissions');
  };

  if (statsError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Error loading dashboard data: {statsError.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 3 }}>
      <Typography variant="h4" component="h1">
        Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Welcome back, {user?.fullName || user?.email || 'User'}!
      </Typography>

      {/* Statistics Cards — only render cards for which the backend returned data */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: isMobile ? 2 : 3, mb: 4 }}>
        {statsData?.data?.users && (
          <>
            <StatCard
              title="Users"
              value={statsData.data.users.total}
              icon={<PeopleIcon color="primary" />}
              isLoading={isLoadingStats}
              onClick={() => handleNavigateToUsers()}
            />
            <StatCard
              title="Active Users"
              value={statsData.data.users.active}
              icon={<SupervisedUserCircleIcon color="success" />}
              isLoading={isLoadingStats}
              onClick={() => handleNavigateToUsers('active')}
            />
            <StatCard
              title="Blocked Users"
              value={statsData.data.users.blocked}
              icon={<BlockIcon color="error" />}
              isLoading={isLoadingStats}
              onClick={() => handleNavigateToUsers('blocked')}
            />
          </>
        )}
        {statsData?.data?.roles && (
          <StatCard
            title="Roles"
            value={statsData.data.roles.total}
            icon={<SecurityIcon color="primary" />}
            isLoading={isLoadingStats}
            onClick={handleNavigateToRoles}
          />
        )}
        {statsData?.data?.permissions && (
          <StatCard
            title="Permissions"
            value={statsData.data.permissions.total}
            icon={<AdminPanelSettingsIcon color="secondary" />}
            isLoading={isLoadingStats}
            onClick={handleNavigateToPermissions}
          />
        )}
      </Box>

      <Grid container spacing={isMobile ? 2 : 3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Box
              sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}
            >
              {hasPermission('auth-service', 'user', 'list') && (
                <Button
                  variant="outlined"
                  startIcon={<PeopleIcon />}
                  component={Link}
                  to="/users"
                >
                  Manage Users
                </Button>
              )}
              {hasPermission('auth-service', 'role', 'list') && (
                <Button
                  variant="outlined"
                  startIcon={<SecurityIcon />}
                  component={Link}
                  to="/roles"
                >
                  Manage Roles
                </Button>
              )}
              {hasPermission('auth-service', 'permission', 'list') && (
                <Button
                  variant="outlined"
                  startIcon={<AdminPanelSettingsIcon />}
                  component={Link}
                  to="/permissions"
                >
                  Manage Permissions
                </Button>
              )}
              {syncData?.data?.dropboxConnected && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={exportMutation.isLoading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                  onClick={handleExportToDropbox}
                  disabled={exportMutation.isLoading}
                >
                  {exportMutation.isLoading ? 'Exporting...' : 'Export to Dropbox'}
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              System Information
            </Typography>
            <Typography variant="body1">
              User and role management system.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Version: 1.0.0
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// StatCard component
const StatCard = ({ title, value, icon, isLoading, onClick }) => {
  return (
    <Paper
      sx={{
        p: 2,
        flex: '1 1 0',
        minWidth: 160,
        maxWidth: 260,
        '@media (max-width: 400px)': {
          flex: '1 1 100%',
          minWidth: 'unset',
          maxWidth: '100%',
        },
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        '&:hover': onClick ? { transform: 'translateY(-2px)', boxShadow: 3 } : {},
      }}
      onClick={onClick}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" component="div">
            {isLoading ? <CircularProgress size={20} /> : value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
        </Box>
        <Box sx={{ color: 'primary.main' }}>
          {icon}
        </Box>
      </Box>
    </Paper>
  );
};

export default Dashboard;
